'use es6';

import { useCallback, useEffect, useMemo, useState } from 'react';
import tinymce from 'tinymce';
import { CONTENT_GENERATION_TEXT_SELECTED, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE, CONTENT_GENERATION_MENU_OPEN } from '../constants/usageTracking';
import { useAtomValue } from '../data/atomUtils';
import { autocompleteActiveRangeAtom } from '../data/autocompleteAtoms';
import { newlinesToParagraphs } from '../hscontentgeneration/utils';
import { isWhitespace } from '../utils';
import { getAnnotatedSelectionContextAsPlainText } from '../utils/dom';
import { trackInteraction } from '../utils/usageTracking';
import TinyMcePopover from './TinyMcePopover';
import CopilotDropdownButton from 'ai-components-ui-library/components/buttons/CopilotDropdownButton';
import { DropdownCaret } from 'foundations-components/transitional/components/AbstractDropdown';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NOT_USING_TRACKER } from 'ai-components-ui-library/hooks/useTracker';
import { NoAuthGatesProvider } from 'ai-components-ui-library/hooks/useIsUngated';
import HighlightedTextFloaty from './HighlightedTextFloaty';
import { useTinyMcePopoverLifecycle } from '../hooks/useTinyMcePopoverLifecycle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OverlayButton = ({
  onClick,
  editor,
  onRemove
}) => {
  const blockEvent = useCallback(evt => {
    // Prevent default to avoid blurring the editor in safari
    // onRemove handler runs if this isn't set
    evt.preventDefault();
  }, []);
  useTinyMcePopoverLifecycle({
    editor,
    onRemove
  });
  return /*#__PURE__*/_jsx(HighlightedTextFloaty, {
    editor: editor,
    placement: "bottom",
    children: /*#__PURE__*/_jsxs(CopilotDropdownButton, {
      onMouseDown: blockEvent,
      onClick: onClick,
      use: "secondary",
      usageTracker: NOT_USING_TRACKER,
      size: "xs",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "TinymcePlugins.hscopilotcollaboration.refine"
      }), /*#__PURE__*/_jsx(DropdownCaret, {})]
    })
  });
};
function useBookmark(editor) {
  const [bookmarkId, setBookmarkId] = useState(null);
  const createBookmark = useCallback(() => {
    const bookmark = editor.selection.getBookmark();
    setBookmarkId(bookmark.id);
  }, [editor]);
  const bookmarkEndNode = useMemo(() => {
    if (!bookmarkId) {
      return null;
    }
    return editor.dom.get(`${bookmarkId}_end`);
  }, [bookmarkId, editor]);
  const cleanupBookmark = useCallback(() => {
    if (!bookmarkId) {
      return;
    }
    const startNode = editor.dom.get(`${bookmarkId}_start`);
    const endNode = editor.dom.get(`${bookmarkId}_end`);
    if (startNode) {
      editor.dom.remove(startNode);
    }
    if (endNode) {
      editor.dom.remove(endNode);
    }
    setBookmarkId(null);
  }, [bookmarkId, editor]);
  return {
    bookmarkEndNode,
    createBookmark,
    cleanupBookmark
  };
}
const OverlayContent = ({
  editor,
  selectionCommands,
  onInsert,
  selectedText,
  editorContext,
  ContentAssistanceToolbar,
  onClosePopover,
  setEditorContext,
  bookmarkEndNode,
  createBookmark,
  cleanupBookmark,
  brandVoiceOptions
}) => {
  var _editor$settings$gate;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeOtherInlineTargetPopovers = useCallback(() => editor.fire('hsRemoveInlineTargets'), [editor]);
  const gates = (_editor$settings$gate = editor.settings.gates) !== null && _editor$settings$gate !== void 0 ? _editor$settings$gate : [];
  const handleTrackInteraction = useCallback((action, props = {}, eventKey = CONTENT_GENERATION_INLINE_EDIT_INTERACTION) => {
    const meta = Object.assign({
      actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE
    }, props);
    trackInteraction(editor, eventKey, action, meta);
  }, [editor]);

  // Track selection, every time popover is opened (icon is shown).
  useEffect(() => {
    handleTrackInteraction(CONTENT_GENERATION_TEXT_SELECTED);
  }, [handleTrackInteraction]);
  const handleButtonClick = useCallback(() => {
    createBookmark();
    setPopoverOpen(true);
    setEditorContext();
    closeOtherInlineTargetPopovers();
    handleTrackInteraction(CONTENT_GENERATION_MENU_OPEN);
  }, [setEditorContext, closeOtherInlineTargetPopovers, handleTrackInteraction, createBookmark]);
  const handleOpenChange = useCallback(event => {
    if (!event.target.value) {
      setPopoverOpen(false);
      cleanupBookmark();
    }
  }, [cleanupBookmark]);
  const blockEvent = useCallback(evt => {
    // Prevent default to avoid blurring the editor in safari
    // onRemove handler runs if this isn't set
    evt.preventDefault();
  }, []);
  const handlePopoverRemove = useCallback(() => {
    cleanupBookmark();
    onClosePopover();
  }, [cleanupBookmark, onClosePopover]);
  useEffect(() => {
    const handleOpenEvent = () => {
      handleButtonClick();
    };
    editor.on('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    return () => {
      editor.off('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    };
  }, [editor, handleButtonClick]);
  return /*#__PURE__*/_jsxs(NoAuthGatesProvider, {
    gates: gates,
    children: [!popoverOpen ? /*#__PURE__*/_jsx(OverlayButton, {
      editor: editor,
      onRemove: onClosePopover,
      onClick: handleButtonClick
    }) : null, /*#__PURE__*/_jsx(TinyMcePopover, {
      editor: editor,
      onRemove: handlePopoverRemove,
      placement: "bottom right",
      use: "unstyled",
      content: /*#__PURE__*/_jsx("div", {
        onMouseDown: blockEvent,
        style: {
          minWidth: 120,
          minHeight: 165
        },
        children: /*#__PURE__*/_jsx(ContentAssistanceToolbar, {
          brandVoiceOptions: brandVoiceOptions,
          selectedText: selectedText,
          selectionCommands: selectionCommands,
          editorContext: editorContext,
          onTextChange: onInsert,
          onClosePopover: handlePopoverRemove,
          trackInteraction: handleTrackInteraction,
          tooltipClassName: 'mce-content-assistance-inline-toolbar-tooltip'
        })
      }),
      target: bookmarkEndNode,
      onOpenChange: handleOpenChange,
      open: popoverOpen
    })]
  });
};
const useIsEditorActive = editor => {
  const [isActive, setIsActive] = useState('false');
  const activateHandler = useCallback(() => {
    setIsActive(true);
  }, []);
  const deactivateHandler = useCallback(() => {
    setIsActive(false);
  }, []);
  useEffect(() => {
    editor.on('activate', activateHandler);
    editor.on('deactivate', deactivateHandler);
    return () => {
      editor.off('activate', activateHandler);
      editor.off('deactivate', deactivateHandler);
    };
  }, [editor, activateHandler, deactivateHandler]);
  return {
    isActive
  };
};
const useHandlers = editor => {
  const [selectionText, setSelectionText] = useState('');
  const [editorContext, setEditorContext] = useState('');
  const [isMouseDown, setIsMouseDown] = useState(false);
  const updateEditorContext = useCallback(() => {
    const range = editor.selection.getRng();
    const context = getAnnotatedSelectionContextAsPlainText(editor, range);
    setEditorContext(context);
    // Make sure to reset the range for Safari
    editor.selection.setRng(range);
  }, [editor]);
  const {
    onMouseDown,
    onMouseUp,
    handleSelectionChange
  } = useMemo(() => {
    let isExpectingNodeChangeEvent = false;
    function onMouseDownHandler() {
      isExpectingNodeChangeEvent = true;
      setIsMouseDown(true);
    }
    function selectionChangeHandler() {
      isExpectingNodeChangeEvent = false;
      const text = editor.selection.getContent({
        format: 'text'
      });
      const isCollapsed = editor.selection.isCollapsed();
      if (isWhitespace(text) || isCollapsed) {
        setSelectionText('');
        setEditorContext('');
        return;
      }
      setSelectionText(text);
    }
    function onMouseUpHandler() {
      setIsMouseDown(false);
      if (isExpectingNodeChangeEvent) {
        isExpectingNodeChangeEvent = false;
        selectionChangeHandler();
      }
    }
    return {
      onMouseDown: onMouseDownHandler,
      onMouseUp: onMouseUpHandler,
      handleSelectionChange: selectionChangeHandler
    };
  }, [editor]);
  const resetSelection = useCallback(() => {
    setSelectionText('');
    setEditorContext('');
  }, []);
  useEffect(() => {
    editor.on('NodeChange', handleSelectionChange);
    editor.on('mousedown', onMouseDown);
    // Handle mouseup that can occur both on the child frame and on the parent app
    editor.on('mouseup', onMouseUp);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      editor.off('NodeChange', handleSelectionChange);
      editor.off('mousedown', onMouseDown);
      editor.off('mouseup', onMouseUp);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseDown, onMouseUp, handleSelectionChange, editor]);
  return {
    selectionText,
    editorContext,
    setEditorContext: updateEditorContext,
    resetSelection,
    isUpdatingSelectionByMouse: isMouseDown
  };
};
const ContentAssistancePopoverManager = ({
  editor,
  selectionCommands,
  ContentAssistanceToolbar,
  brandVoiceOptions
}) => {
  const {
    selectionText,
    resetSelection,
    editorContext,
    setEditorContext,
    isUpdatingSelectionByMouse
  } = useHandlers(editor);
  const {
    bookmarkEndNode,
    createBookmark,
    cleanupBookmark
  } = useBookmark(editor);
  const handleInsert = useCallback(content => {
    cleanupBookmark();
    editor.undoManager.transact(() => {
      const processedContent = newlinesToParagraphs(content);
      editor.insertContent(processedContent);
    });
  }, [editor, cleanupBookmark]);
  useEffect(() => {
    if (!selectionText) {
      cleanupBookmark();
    }
  }, [selectionText, cleanupBookmark]);
  if (!selectionText || editor.id !== tinymce.activeEditor.id ||
  // Do not show popover if the user is changing the text selection by mouse
  isUpdatingSelectionByMouse) {
    return null;
  }
  return /*#__PURE__*/_jsx(OverlayContent, {
    editor: editor,
    editorContext: editorContext,
    selectionCommands: selectionCommands,
    onInsert: handleInsert,
    selectedText: selectionText,
    onClosePopover: resetSelection,
    ContentAssistanceToolbar: ContentAssistanceToolbar,
    setEditorContext: setEditorContext,
    bookmarkEndNode: bookmarkEndNode,
    createBookmark: createBookmark,
    cleanupBookmark: cleanupBookmark,
    brandVoiceOptions: brandVoiceOptions
  });
};
const ActiveEditorGuard = ({
  editor,
  ContentAssistanceToolbar,
  selectionCommands,
  brandVoiceOptions
}) => {
  const {
    isActive
  } = useIsEditorActive(editor);
  const autocompleteRange = useAtomValue(autocompleteActiveRangeAtom);
  // TinyMCE menu edge case: Don't show if the TinyMCE Content Assistant menu is open
  if (!isActive || autocompleteRange) {
    return null;
  }
  return /*#__PURE__*/_jsx(ContentAssistancePopoverManager, {
    editor: editor,
    ContentAssistanceToolbar: ContentAssistanceToolbar,
    selectionCommands: selectionCommands,
    brandVoiceOptions: brandVoiceOptions
  });
};
export default ActiveEditorGuard;